import { MediaItem, type MediaType, OkResponse } from '@deizi/shared';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import {
  type UploadHandle,
  type UploadStatus,
  progressLabel,
  startUpload,
} from '../../lib/tus-upload';

export interface QueuedMedia {
  key: string;
  file: File;
  type: MediaType;
  status: UploadStatus | 'added';
  sent: number;
  total: number;
  error?: string;
  handle?: UploadHandle;
  /** Local preview of a photo (object URL). */
  preview?: string;
  /** The gallery item once added. */
  mediaId?: string;
}

/** Guesses the type from the file: videos are usually timelapses. */
export const typeFor = (file: File, fallback: MediaType): MediaType =>
  file.type.startsWith('video/') || /\.(mp4|mov|m4v)$/i.test(file.name) ? 'timelapse' : fallback;

/**
 * Resumable uploads of photos and videos to a product's gallery: each file uploads with tus and is
 * added to the gallery as soon as it arrives (`POST /products/{id}/media`).
 */
export function useMediaQueue(
  target: { productId: string; variantId: string | null } | null,
  onAdded: () => void,
) {
  const [items, setItems] = useState<QueuedMedia[]>([]);
  const ref = useRef(items);
  const targetRef = useRef(target);
  useEffect(() => {
    ref.current = items;
    targetRef.current = target;
  });
  const patch = (key: string, p: Partial<QueuedMedia>) =>
    setItems((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));

  useEffect(
    () => () => {
      for (const i of ref.current) if (i.preview) URL.revokeObjectURL(i.preview);
    },
    [],
  );

  const add = (picked: FileList | File[] | null, type: MediaType) => {
    const t = targetRef.current;
    if (!t) return;
    for (const file of Array.from(picked ?? [])) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      const handle = startUpload(file, 'media', {
        progress: (sent, total) => patch(key, { sent, total }),
        status: (status, error) => patch(key, { status, error }),
      });
      void handle.done
        .then(async (stored) => {
          // The type chosen when the upload finished (it can change while uploading).
          const now = ref.current.find((x) => x.key === key);
          const added = await api.post(
            `/products/${t.productId}/media`,
            MediaItem,
            {
              fileId: stored.id,
              type: now?.type ?? type,
              variantId: t.variantId,
            },
            { idempotencyKey: `media-${stored.id}` },
          );
          patch(key, { status: 'added', mediaId: added.id });
          onAdded();
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.message === 'cancelado') return;
          patch(key, { status: 'error', error: errorMessage(err) });
        });
      const preview =
        file.type.startsWith('image/') && !/hei[cf]$/i.test(file.name)
          ? URL.createObjectURL(file)
          : undefined;
      setItems((list) => [
        ...list,
        {
          key,
          file,
          type: typeFor(file, type),
          status: 'uploading',
          sent: 0,
          total: file.size,
          handle,
          preview,
        },
      ]);
    }
  };

  const active = items.filter(
    (i) => i.status === 'uploading' || i.status === 'offline' || i.status === 'paused',
  );
  return {
    items,
    add,
    setType: (key: string, type: MediaType) => {
      patch(key, { type });
      const id = ref.current.find((x) => x.key === key)?.mediaId;
      if (id) void api.patch(`/media/${id}`, MediaItem, { type }).then(onAdded, () => undefined);
    },
    /** Stops the upload, or removes the item from the gallery when it was already added. */
    remove: (key: string) => {
      const it = ref.current.find((x) => x.key === key);
      if (it && it.status !== 'added') it.handle?.abort();
      if (it?.mediaId)
        void api.delete(`/media/${it.mediaId}`, OkResponse).then(onAdded, () => undefined);
      if (it?.preview) URL.revokeObjectURL(it.preview);
      setItems((list) => list.filter((x) => x.key !== key));
    },
    pauseAll: () => {
      for (const i of active) if (i.status !== 'paused') i.handle?.pause();
    },
    resumeAll: () => {
      for (const i of active) if (i.status === 'paused') i.handle?.resume();
    },
    active: active.length,
    added: items.filter((i) => i.status === 'added').length,
    failed: items.filter((i) => i.status === 'error').length,
    sent: items.reduce((a, i) => a + i.sent, 0),
    total: items.reduce((a, i) => a + i.total, 0),
  };
}

export function statusText(i: QueuedMedia): string {
  if (i.status === 'added') return 'Na galeria';
  if (i.status === 'done') return 'Adicionando…';
  if (i.status === 'error') return i.error ?? 'Falhou';
  if (i.status === 'paused') return `Pausado · ${progressLabel(i.sent, i.total)}`;
  if (i.status === 'offline') return 'Sem sinal. Continua quando a conexão voltar.';
  return progressLabel(i.sent, i.total);
}
